import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveFavorites, type FavoritesData } from '../services/entityApi';
import type { Favorite } from '../services/favoritesStorage';
import type { Logger } from '../types';
import type { favoritesQueryKey } from './useFavoritesQuery';

function logFavoriteDiffs(prev: Favorite[], next: Favorite[], logger: Logger) {
  const prevPathnames = new Set(prev.map((f) => f.pathname));
  const nextPathnames = new Set(next.map((f) => f.pathname));

  next
    .filter((f) => !prevPathnames.has(f.pathname))
    .forEach((f) => logger.info('Favorite added', { label: f.label, pathname: f.pathname }));

  prev
    .filter((f) => !nextPathnames.has(f.pathname))
    .forEach((f) => logger.info('Favorite removed', { label: f.label, pathname: f.pathname }));

  next
    .filter((f) => {
      const prevFav = prev.find((p) => p.pathname === f.pathname);
      return prevFav !== undefined && prevFav.label !== f.label;
    })
    .forEach((f) => {
      const prevFav = prev.find((p) => p.pathname === f.pathname)!;
      logger.info('Favorite renamed', {
        pathname: f.pathname,
        previous_label: prevFav.label,
        new_label: f.label,
      });
    });
}

export function useFavoritesMutation({
  userUuid,
  token,
  entityId,
  queryKey,
  logger,
  onMutateStart,
  onSuccess,
  onError,
}: {
  userUuid: string | undefined;
  token: string | null | undefined;
  entityId: number | undefined;
  queryKey: ReturnType<typeof favoritesQueryKey>;
  logger: Logger;
  onMutateStart?: () => void;
  onSuccess: () => void;
  onError: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { next: Favorite[]; prev: Favorite[] }>({
    mutationFn: async ({ next }) => {
      const current = queryClient.getQueryData<FavoritesData>(queryKey);
      return saveFavorites(
        userUuid!,
        token!,
        { favoritesByEntity: { ...current?.favoritesByEntity, [String(entityId)]: next } },
        logger
      );
    },
    onMutate: async ({ next }) => {
      onMutateStart?.();
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<FavoritesData>(queryKey, (old) => ({
        favoritesByEntity: { ...old?.favoritesByEntity, [String(entityId)]: next },
      }));
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey });
      onError();
    },
    onSuccess: (_data, { next, prev }) => {
      logFavoriteDiffs(prev, next, logger);
      onSuccess();
    },
  });
}
